"use client";

import Button, { geistSans } from "@/components/atoms/buttons/Button";
import { toast } from "@/components/atoms/toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { createAuthSchemas, ResetPasswordSchema } from "@/lib/validation/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import Link from "next/link";
import { useState, useTransition } from "react";
import { useForm } from "react-hook-form";

type ForgotPasswordFormProps = {
  locale: Locales;
};

const ForgotPasswordForm = ({ locale }: ForgotPasswordFormProps) => {
  const { resetPasswordSchema } = createAuthSchemas(locale);
  const [isPending, startTransition] = useTransition();
  const [isSuccessful, setIsSuccessful] = useState(false);
  const form = useForm<ResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const { handleSubmit, control } = form;

  const onSubmit = (data: ResetPasswordSchema) => {
    startTransition(async () => {
      try {
        setIsSuccessful(false);

        const baseUrl = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || "http://localhost:9000";
        const apiKey = process.env.NEXT_PUBLIC_MEDUSA_PUBLISHABLE_KEY;

        const redirectUrl = window.location.origin + getPermalink(locale, "reset-password");

        const response = await fetch(`${baseUrl}/store/custom/request-passwordreset`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-publishable-api-key": apiKey ?? "",
          },
          body: JSON.stringify({
            email: data.email,
            url: redirectUrl,
            lang: locale,
          }),
        });

        const body = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (body?.error?.message === "customer not found") {
            setIsSuccessful(true);
            return;
          }

          throw new Error(body?.error?.message || "Unknown error");
        }
        setIsSuccessful(true);
      } catch (error) {
        console.error("Error submitting form:", error);
        setIsSuccessful(false);
        toast.error({ title: __("Something went wrong. Please try again.", locale) });
      }
    });
  };

  return (
    <div className="font-cabinet-grotesk text-dark-primary mx-auto mt-10 w-full max-w-[672px]">
      <h3 className="text-2xl font-bold">{__("Verify my email address", locale)}</h3>
      {isSuccessful && (
        <p className="text-primary-light">
          {__(
            "If an account is associated with this address, you will receive an email to reset your password.",
            locale
          )}
          <br />
          {__(" Please check your inbox.", locale)}
        </p>
      )}
      {!isSuccessful && (
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className={cn("mt-6 space-y-6", geistSans.className)}>
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <FormItem className="w-full gap-1">
                  <div className="flex flex-col gap-2.5">
                    <FormLabel className="mb-0 text-base font-medium">
                      {__("Email Address", locale)} <span className="text-pink-primary">*</span>
                    </FormLabel>

                    <FormControl>
                      <Input
                        disabled={isPending || isSuccessful}
                        type="email"
                        className="border-border-light aria-invalid:border-pink-primary aria-invalid:text-pink-primary focus-visible:border-primary-light focus-visible:ring-primary-light/50 placeholder:text-primary-light h-[48px] placeholder:text-sm placeholder:font-medium focus-visible:ring-[2px]"
                        placeholder={__("Email Address", locale)}
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button disabled={isPending || isSuccessful} className="h-11 w-full text-base font-semibold" type="submit">
              {isPending && <Loader2 className="mr-2 animate-spin" />} {__("Send Instructions", locale)}
            </Button>
          </form>
        </Form>
      )}

      <p className="mt-6">
        {__("Already registered ?", locale)}{" "}
        <Link
          href={getPermalink(locale, "login")}
          className="text-pink-primary focus-visible:ring-pink-primary/50 text-start underline outline-none focus-visible:ring-[2px] focus-visible:ring-offset-2"
        >
          {__("Login", locale)}
        </Link>
      </p>
    </div>
  );
};

export default ForgotPasswordForm;
